(() => {
  'use strict';

  const roots = document.querySelectorAll('[data-f2w-trainer-directory]');
  if (!roots.length) return;

  const normalize = (value) => String(value || '').toLocaleLowerCase('nl-NL').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  const parseList = (value) => {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) return value.flatMap(parseList).filter(Boolean);
    const text = String(value).trim();
    if (!text) return [];
    if ((text.startsWith('[') && text.endsWith(']')) || (text.startsWith('{') && text.endsWith('}'))) {
      try { const parsed = JSON.parse(text); if (Array.isArray(parsed)) return parsed.flatMap(parseList).filter(Boolean); if (parsed && typeof parsed === 'object') return Object.values(parsed).flatMap(parseList).filter(Boolean); } catch (error) { /* Plain text fallback. */ }
    }
    return text.split(/[|,\n]+/).map((item) => item.trim()).filter(Boolean);
  };
  const richTextToPlainText = (value) => { const walk = (node) => { if (node === null || node === undefined) return ''; if (typeof node === 'string') return node; if (Array.isArray(node)) return node.map(walk).join(' '); if (typeof node !== 'object') return ''; if (typeof node.value === 'string') return node.value; return walk(node.children || node.content || Object.values(node)); }; try { return walk(JSON.parse(value)).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(); } catch (error) { return ''; } };
  const text = (field) => { const raw = String(field?.value || '').trim(); if (!raw) return ''; const parsed = raw.startsWith('{') || raw.startsWith('[') ? richTextToPlainText(raw) : ''; return (parsed || raw).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(); };
  const unique = (values) => [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))];
  const chunks = (values, size) => { const result = []; for (let index = 0; index < values.length; index += size) result.push(values.slice(index, index + size)); return result; };
  const nativeUrl = (value) => { if (!value) return ''; try { const url = new URL(String(value), window.location.origin); if (!['http:', 'https:'].includes(url.protocol)) return ''; const root = window.Shopify?.routes?.root || '/'; const prefix = root === '/' ? '' : root.replace(/\/$/, ''); let path = url.pathname; if (/^\/[a-z]{2}(\/|$)/i.test(path)) path = path.replace(/^\/[a-z]{2}/i, ''); if (prefix && path.startsWith(`${prefix}/`)) path = path.slice(prefix.length); return `${prefix}${path}${url.search}${url.hash}`; } catch (error) { return ''; } };
  const shuffle = (items) => { const result = [...items]; for (let index = result.length - 1; index > 0; index -= 1) { const randomIndex = Math.floor(Math.random() * (index + 1)); [result[index], result[randomIndex]] = [result[randomIndex], result[index]]; } return result; };
  const element = (tag, className, value) => { const node = document.createElement(tag); if (className) node.className = className; if (value !== undefined && value !== null) node.textContent = value; return node; };
  const createCountryFlag = (code, label) => { const flagCode = String(code || '').trim().toLowerCase(); const isNation = ['gb-sct', 'gb-eng', 'gb-wls'].includes(flagCode); if (!flagCode || (flagCode.length !== 2 && !isNation)) return null; const wrapper = element('span', 'f2w-country-flag'); const image = document.createElement('img'); image.src = `https://flagcdn.com/w40/${flagCode}.png`; image.srcset = `https://flagcdn.com/w80/${flagCode}.png 2x`; image.alt = String(label || ''); image.width = 20; image.height = 15; image.loading = 'lazy'; image.decoding = 'async'; wrapper.appendChild(image); wrapper.appendChild(document.createTextNode('\u00a0')); return wrapper; };
  const request = async (endpoint, token, query, variables) => { const response = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token }, body: JSON.stringify({ query, variables }) }); if (!response.ok) throw new Error('Storefront request failed'); const json = await response.json(); if (Array.isArray(json.errors) && json.errors.length) throw new Error('Storefront query failed'); return json.data || {}; };

  const LABEL_QUERY = `query F2WTrainerLabels($ids: [ID!]!, $language: LanguageCode!) @inContext(language: $language) { nodes(ids: $ids) { ... on Metaobject { id handle onlineStoreUrl name: field(key: "name") { value } code: field(key: "code") { value } } } }`;
  const TRAINER_QUERY = `
    query F2WTrainersByIds($ids: [ID!]!, $language: LanguageCode!) @inContext(language: $language) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id handle onlineStoreUrl
          name: field(key: "name") { value }
          role: field(key: "role") { value }
          country: field(key: "country") { reference { ... on Metaobject { id handle onlineStoreUrl name: field(key: "name") { value } code: field(key: "code") { value } } } }
          sports: field(key: "sports") { references(first: 50) { nodes { ... on Metaobject { id handle onlineStoreUrl name: field(key: "name") { value } } } } }
          homeGym: field(key: "home_gym") { reference { ... on Metaobject { id handle onlineStoreUrl name: field(key: "name") { value } } } }
          specialization: field(key: "specialization") { value }
          notableFighters: field(key: "notable_fighters_coached") { references(first: 50) { nodes { ... on Metaobject { id handle onlineStoreUrl name: field(key: "name") { value } } } } }
          profileIntro: field(key: "profile_intro") { value }
          profileContent: field(key: "profile_content") { value }
          approach: field(key: "approach") { value }
          resolvedImageUrl: field(key: "resolved_image_url") { value }
        }
      }
    }
  `;

  const parseIndex = (payload) => { if (!payload || payload.v !== 1 || !Number.isInteger(payload.count) || !Array.isArray(payload.rows) || payload.rows.length !== payload.count) throw new Error('Invalid Trainers index'); const seen = new Set(); return payload.rows.map((row) => { const id = String(row?.[0] || '').trim(); if (!id.startsWith('gid://shopify/Metaobject/') || seen.has(id)) throw new Error('Invalid Trainers index row'); seen.add(id); return { id, handle: String(row?.[1] || '').trim(), countryId: String(row?.[2] || '').trim(), sportIds: unique(Array.isArray(row?.[3]) ? row[3] : []), homeGymId: String(row?.[4] || '').trim(), roles: parseList(row?.[5]).map(normalize) }; }).filter((record) => record.roles.includes('trainer')); };
  const loadIndexPayload = async (root, indexUrl) => { const inline = root.querySelector('[data-f2w-index-json]'); const raw = inline?.textContent?.trim() || ''; if (raw && raw !== 'null') { let payload = JSON.parse(raw); if (typeof payload === 'string') payload = JSON.parse(payload); if (payload) return payload; } if (!indexUrl) throw new Error('Missing Trainers loader configuration'); const response = await fetch(indexUrl, { credentials: 'same-origin' }); if (!response.ok) throw new Error('Trainers index request failed'); return response.json(); };

  roots.forEach((root) => {
    if (root.dataset.f2wInitialized === 'true') return;
    root.dataset.f2wInitialized = 'true';
    const token = root.dataset.storefrontToken || '';
    const language = String(root.dataset.language || document.documentElement?.lang || 'nl').split('-')[0].toUpperCase() || 'NL';
    const shop = root.dataset.shopDomain || window.Shopify?.shop || window.location.hostname;
    const endpoint = root.dataset.storefrontEndpoint || `https://${shop}/api/${root.dataset.apiVersion || '2024-10'}/graphql.json`;
    const indexUrl = root.dataset.indexUrl || '';
    const search = root.querySelector('[data-f2w-trainer-filter-search]');
    const countrySelect = root.querySelector('[data-f2w-trainer-filter-country]');
    const sportSelect = root.querySelector('[data-f2w-trainer-filter-sport]');
    const gymSelect = root.querySelector('[data-f2w-trainer-filter-gym]');
    const filterContainer = root.querySelector('[data-f2w-directory-filters]');
    const results = root.querySelector('[data-f2w-trainer-results]');
    const count = root.querySelector('[data-f2w-trainer-results-count]');
    const pagination = root.querySelector('[data-f2w-trainer-pagination]');
    const perPage = 24;
    const countFormatter = new Intl.NumberFormat(root.dataset.language || document.documentElement?.lang || 'nl');
    root.querySelectorAll('[data-f2w-trainer-snapshot-value]').forEach((value) => {
      const number = Number(value.textContent.trim());
      if (Number.isFinite(number)) value.textContent = countFormatter.format(number);
    });
    let currentPage = 1;
    const loading = root.querySelector('[data-f2w-trainer-loading]');
    const errorElement = root.querySelector('[data-f2w-trainer-error]');
    const empty = root.querySelector('[data-f2w-trainer-empty]');
    const labels = { results: root.dataset.labelResults || '', loading: root.dataset.labelLoading || '', error: root.dataset.labelError || '', empty: root.dataset.labelEmpty || '', viewProfile: root.dataset.labelViewProfile || '', notable: root.dataset.labelNotable || '' };
    const roleLabels = { trainer: root.dataset.labelRoleTrainer || 'Trainer', master: root.dataset.labelRoleMaster || 'Meester', founder: root.dataset.labelRoleFounder || 'Grondlegger', promoter: root.dataset.labelRolePromoter || 'Promotor', official: root.dataset.labelRoleOfficial || 'Official', corner_man: root.dataset.labelRoleCornerMan || 'Hoekman' };
    const editorial = { headCoaches: [root.querySelector('[data-f2w-trainer-editorial-head-coaches-section]'), root.querySelector('[data-f2w-trainer-editorial-head-coaches]')], boxing: [root.querySelector('[data-f2w-trainer-editorial-boxing-section]'), root.querySelector('[data-f2w-trainer-editorial-boxing]')], kickboxing: [root.querySelector('[data-f2w-trainer-editorial-kickboxing-section]'), root.querySelector('[data-f2w-trainer-editorial-kickboxing]')], mma: [root.querySelector('[data-f2w-trainer-editorial-mma-section]'), root.querySelector('[data-f2w-trainer-editorial-mma]')] };
    let indexRecords = [];
    let metadata = new Map();
    let trainers = [];

    const setElementVisibility = (node, visible) => { if (!node) return; node.hidden = !visible; node.style.setProperty('display', visible ? '' : 'none', 'important'); node.setAttribute('aria-hidden', visible ? 'false' : 'true'); };
    const setLoading = (visible) => { if (loading) { setElementVisibility(loading, visible); loading.textContent = labels.loading; } };
    const setError = (message) => { if (errorElement) { setElementVisibility(errorElement, Boolean(message)); errorElement.textContent = message || ''; } };
    const labelFor = (id) => metadata.get(id)?.label || '';
    const codeFor = (id) => metadata.get(id)?.code || '';
    const relation = (node) => ({ id: node?.id || '', handle: node?.handle || '', label: text(node?.name) || node?.handle || '', code: text(node?.code), url: nativeUrl(node?.onlineStoreUrl) });
    const relationNodes = (field) => field?.references?.nodes || [];
    const setOptions = (select, firstLabel, options) => { if (!select) return; select.replaceChildren(); const first = element('option', '', firstLabel); first.value = ''; select.appendChild(first); options.forEach((option) => { const node = element('option', '', option.label); node.value = option.value; select.appendChild(node); }); };
    const loadMetadata = async (ids) => { const allIds = unique(ids); if (!allIds.length) return new Map(); const pages = await Promise.all(chunks(allIds, 100).map((batch) => request(endpoint, token, LABEL_QUERY, { ids: batch, language }))); const result = new Map(); pages.flatMap((page) => page.nodes || []).forEach((node) => { if (node?.id) result.set(node.id, { id: node.id, handle: node.handle || '', label: text(node.name) || node.handle || '', code: text(node.code), url: nativeUrl(node.onlineStoreUrl) }); }); return result; };
    const optionValues = (records, key) => {
      if (key === 'sport') return unique(records.flatMap((record) => record.sportIds));
      if (key === 'country') return unique(records.map((record) => record.countryId));
      return unique(records.map((record) => record.homeGymId));
    };

    const matchesIndexRecord = (record, current, except) => {
      if (except !== 'sport' && current.sport && !record.sportIds.includes(current.sport)) return false;
      if (except !== 'country' && current.country && record.countryId !== current.country) return false;
      if (except !== 'gym' && current.gym && record.homeGymId !== current.gym) return false;
      return true;
    };

    const syncFilterOptions = () => {
      const current = currentFilters();
      const configs = [
        { key: 'sport', select: sportSelect, label: root.dataset.allSportsLabel || '' },
        { key: 'country', select: countrySelect, label: root.dataset.allCountriesLabel || '' },
        { key: 'gym', select: gymSelect, label: root.dataset.allGymsLabel || '' }
      ];

      const nextOptions = configs.map((config) => {
        const availableRecords = config.key === 'country'
          ? indexRecords
          : indexRecords.filter((record) => matchesIndexRecord(record, current, config.key));
        const options = optionValues(availableRecords, config.key)
          .map((id) => ({ value: id, label: labelFor(id) || id }))
          .sort((a, b) => a.label.localeCompare(b.label, 'nl-NL'));
        return { ...config, options };
      });

      nextOptions.forEach((config) => {
        setOptions(config.select, config.label, config.options);
        const stillAvailable = config.options.some((option) => {
          return option.value === current[config.key];
        });
        if (config.select) {
          config.select.value = stillAvailable ? current[config.key] : '';
        }
      });
      renderChips();
    };

    const buildFilters = () => {
      syncFilterOptions();
    };
    const mapTrainer = (node, record) => { if (!node?.id || !record) return null; const country = node.country?.reference ? relation(node.country.reference) : (record.countryId ? { id: record.countryId, label: labelFor(record.countryId), code: codeFor(record.countryId), url: nativeUrl(metadata.get(record.countryId)?.url) } : null); const sports = (node.sports?.references?.nodes || []).map(relation); const resolvedSports = sports.length ? sports : record.sportIds.map((id) => ({ id, label: labelFor(id), url: nativeUrl(metadata.get(id)?.url) })); const homeGym = node.homeGym?.reference ? relation(node.homeGym.reference) : (record.homeGymId ? { id: record.homeGymId, label: labelFor(record.homeGymId), url: nativeUrl(metadata.get(record.homeGymId)?.url) } : null); const roles = parseList(node.role?.value).map(normalize); return { id: node.id, handle: node.handle || record.handle, url: nativeUrl(node.onlineStoreUrl), name: text(node.name), roles, country, countryId: record.countryId || country?.id, sports: resolvedSports, sportIds: record.sportIds.length ? record.sportIds : resolvedSports.map((item) => item.id), homeGym, gymId: record.homeGymId || homeGym?.id, specialization: text(node.specialization), notableFighters: relationNodes(node.notableFighters).map(relation), intro: text(node.profileIntro) || text(node.profileContent) || text(node.approach), image: text(node.resolvedImageUrl) }; };
    const card = (trainer) => {
      const article = element('article', 'f2w-event-card');
      const link = element(trainer.url ? 'a' : 'div', 'f2w-event-card__link');
      if (trainer.url) link.href = trainer.url;

      const imageWrap = element('div', 'f2w-event-card__image');
      if (trainer.image) {
        const image = document.createElement('img');
        image.src = trainer.image;
        image.alt = trainer.name;
        image.width = 900;
        image.loading = 'lazy';
        image.decoding = 'async';
        imageWrap.appendChild(image);
      } else {
        const placeholder = element('div', 'f2w-event-card__image--placeholder');
        placeholder.appendChild(element('span', '', trainer.name.slice(0, 1)));
        imageWrap.appendChild(placeholder);
      }

      const body = element('div', 'f2w-event-card__body');
      const content = element('div', 'f2w-event-card__content');
      const sportLabels = trainer.sports
        .map((sport) => sport.label)
        .filter(Boolean);
      const countryLabel = trainer.country?.label || '';

      if (sportLabels.length) {
        const topline = element('div', 'f2w-event-card__topline');
        sportLabels.forEach((sportLabel) => {
          topline.appendChild(element(
            'span',
            'f2w-event-card__period f2w-event-card__period--upcoming',
            sportLabel
          ));
        });
        content.appendChild(topline);
      }

      content.appendChild(element('h2', 'f2w-event-card__title', trainer.name));
      if (countryLabel) {
        const countryLine = element('p', 'f2w-event-card__location');
        const flag = createCountryFlag(trainer.country?.code, countryLabel);
        if (flag) countryLine.appendChild(flag);
        countryLine.appendChild(document.createTextNode(countryLabel));
        content.appendChild(countryLine);
      }
      if (trainer.specialization) {
        content.appendChild(element(
          'p',
          'f2w-event-card__location',
          trainer.specialization
        ));
      }
      if (trainer.url) {
        content.appendChild(element(
          'span',
          'f2w-event-card__view',
          labels.viewProfile
        ));
      }

      body.appendChild(content);
      link.append(imageWrap, body);
      article.appendChild(link);
      return article;
    };
    const currentFilters = () => ({ search: search?.value || '', country: countrySelect?.value || '', sport: sportSelect?.value || '', gym: gymSelect?.value || '' });
    const chipShell = root.querySelector('[data-f2w-trainer-chips]');
    const chipRows = {
      country: root.querySelector('[data-f2w-trainer-country-chips]'),
      sport: root.querySelector('[data-f2w-trainer-sport-chips]'),
      gym: root.querySelector('[data-f2w-trainer-gym-chips]')
    };
    const expandedChips = { country: false, sport: false, gym: false };
    const renderChips = () => {
      if (!chipShell || !chipRows.country || !chipRows.sport || !chipRows.gym) return;
      for (const key of ['country', 'sport', 'gym']) {
        const select = { country: countrySelect, sport: sportSelect, gym: gymSelect }[key];
        const row = chipRows[key];
        const options = [...select.options].filter((option) => option.value);
        const ordered = key === 'country'
          ? [...options].sort((a, b) => {
              const priority = (option) => ({ NL: 0, BE: 1 }[codeFor(option.value).toUpperCase()] ?? 2);
              return priority(a) - priority(b) || a.textContent.localeCompare(b.textContent, document.documentElement.lang || 'nl');
            })
          : options;
        const selected = ordered.find((option) => option.value === select.value);
        const quick = ordered.slice(0, 6);
        if (selected && !quick.includes(selected)) quick.push(selected);
        const remaining = ordered.filter((option) => !quick.includes(option));
        const allLabel = { country: root.dataset.allCountriesLabel, sport: root.dataset.allSportsLabel, gym: root.dataset.allGymsLabel }[key];
        const makeChip = (value, label) => {
          const button = element('button', 'f2w-event-card__sport', label);
          button.type = 'button';
          button.setAttribute('aria-pressed', String(select.value === value));
          if (key === 'country' && value) {
            const flag = createCountryFlag(codeFor(value), '');
            if (flag) { flag.setAttribute('aria-hidden', 'true'); flag.querySelector('img').alt = ''; button.prepend(flag); }
          }
          button.addEventListener('click', () => {
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          });
          return button;
        };
        const buttons = [makeChip('', allLabel || '')];
        for (const option of quick) buttons.push(makeChip(option.value, option.textContent.trim()));
        if (expandedChips[key]) {
          for (const option of remaining) buttons.push(makeChip(option.value, option.textContent.trim()));
        }
        if (remaining.length) {
          const toggle = element('button', 'f2w-event-card__sport', expandedChips[key]
            ? root.dataset[`${key}Less`] : root.dataset[`${key}More`]);
          toggle.type = 'button';
          toggle.setAttribute('aria-expanded', String(expandedChips[key]));
          toggle.addEventListener('click', () => {
            expandedChips[key] = !expandedChips[key];
            renderChips();
            chipRows[key].querySelector('[aria-expanded]')?.focus({ preventScroll: true });
          });
          buttons.push(toggle);
        }
        row.replaceChildren(...buttons);
        row.closest('.f2w-sport-detail__country-picker').hidden = !ordered.length;
      }
      chipShell.hidden = false;
      root.dataset.chipsReady = 'true';
    };
    const filtered = () => { const current = currentFilters(); const query = normalize(current.search); return trainers.filter((trainer) => { const searchable = normalize([trainer.name, trainer.specialization, trainer.country?.label, trainer.homeGym?.label, ...trainer.sports.map((item) => item.label), ...trainer.roles.map((role) => roleLabels[role] || role)].join(' ')); return (!query || searchable.includes(query)) && (!current.country || trainer.countryId === current.country) && (!current.sport || trainer.sportIds.includes(current.sport)) && (!current.gym || trainer.gymId === current.gym); }); };
    const renderPagination = (totalResults) => {
      if (!pagination) return;
      const totalPages = Math.ceil(totalResults / perPage);
      pagination.hidden = totalPages <= 1;
      if (pagination.hidden) { pagination.replaceChildren(); return; }
      const go = (target) => {
        currentPage = Math.min(Math.max(target, 1), totalPages);
        render();
        writeUrl();
        pagination.querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
        results?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      const button = (text, target, active, ariaLabel = '') => {
        const node = element('button', 'f2w-event-directory__tab', text);
        node.type = 'button';
        if (ariaLabel) node.setAttribute('aria-label', ariaLabel);
        else node.setAttribute('aria-label', `${root.dataset.labelPage || 'Pagina'} ${target}`);
        if (active) node.setAttribute('aria-current', 'page');
        else node.addEventListener('click', () => go(target));
        return node;
      };
      const controls = [];
      if (currentPage > 1) controls.push(button('◀', currentPage - 1, false, root.dataset.labelPrevious || 'Vorige'));
      const pages = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages]
        .filter((number) => number >= 1 && number <= totalPages))].sort((a, b) => a - b);
      let previous = 0;
      for (const number of pages) {
        if (number - previous > 1) controls.push(element('span', '', '…'));
        controls.push(button(String(number), number, number === currentPage));
        previous = number;
      }
      if (currentPage < totalPages) controls.push(button('▶', currentPage + 1, false, root.dataset.labelNext || 'Volgende'));
      pagination.replaceChildren(...controls);
    };
    const render = () => {
      const current = currentFilters();
      const available = trainers.filter((trainer) =>
        (!current.country || trainer.countryId === current.country) &&
        (!current.sport || trainer.sportIds.includes(current.sport)) &&
        (!current.gym || trainer.gymId === current.gym)
      ).length;
      if (filterContainer) filterContainer.hidden = !(available > 48 || current.search.trim());
      const matches = filtered().sort((a, b) => a.name.localeCompare(b.name, 'nl-NL'));
      const totalPages = Math.max(1, Math.ceil(matches.length / perPage));
      currentPage = Math.min(Math.max(currentPage, 1), totalPages);
      results?.replaceChildren(...matches.slice((currentPage - 1) * perPage, currentPage * perPage).map(card));
      renderPagination(matches.length);
      if (count) count.textContent = [countFormatter.format(matches.length), labels.results].filter(Boolean).join(' ');
      setElementVisibility(empty, matches.length === 0);
    };
    const sportAliases = { boxing: ['boxing', 'boksen'], kickboxing: ['kickboxing', 'kickboksen', 'muay thai', 'muaythai', 'thai boxing', 'thaiboxing'], mma: ['mma', 'mixed martial arts'] };
    const renderGroup = (key, items) => { const [section, grid] = editorial[key]; if (!grid) return; const cards = shuffle(items).slice(0, 2); grid.replaceChildren(...cards.map(card)); if (section) section.hidden = cards.length === 0; };
    const writeUrl = () => { const current = currentFilters(); const url = new URL(window.location.href); Object.entries({ q: current.search.trim(), country: current.country, sport: current.sport, gym: current.gym, page: currentPage > 1 ? String(currentPage) : '' }).forEach(([key, value]) => value ? url.searchParams.set(key, value) : url.searchParams.delete(key)); window.history.replaceState(window.history.state, '', url); };
    const decodeFilterValue = (value) => {
      let decoded = String(value || '');
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const next = decodeURIComponent(decoded);
          if (next === decoded) break;
          decoded = next;
        } catch (error) {
          break;
        }
      }
      return decoded;
    };

    const applyUrl = () => {
      const params = new URLSearchParams(window.location.search);
      if (search) search.value = params.get('q') || '';
      if (countrySelect) countrySelect.value = decodeFilterValue(params.get('country'));
      if (sportSelect) sportSelect.value = decodeFilterValue(params.get('sport'));
      if (gymSelect) gymSelect.value = decodeFilterValue(params.get('gym'));
      const page = Number(params.get('page'));
      currentPage = Number.isSafeInteger(page) && page > 0 ? page : 1;
      syncFilterOptions();
    };
    search?.addEventListener('input', () => { currentPage = 1; render(); writeUrl(); });
    [countrySelect, sportSelect, gymSelect].forEach((select) => select?.addEventListener('change', () => { currentPage = 1; syncFilterOptions(); render(); writeUrl(); }));

    (async () => { setLoading(true); try { if (!token) throw new Error('Missing Trainers loader configuration'); indexRecords = parseIndex(await loadIndexPayload(root, indexUrl)); metadata = await loadMetadata(indexRecords.flatMap((record) => [record.countryId, ...record.sportIds, record.homeGymId])); buildFilters(); applyUrl(); const ids = indexRecords.map((record) => record.id); const pages = await Promise.all(chunks(ids, 100).map((batch) => request(endpoint, token, TRAINER_QUERY, { ids: batch, language }))); const nodes = pages.flatMap((page) => page.nodes || []).filter(Boolean); const relationIds = nodes.flatMap((node) => [node.country?.reference?.id || '', node.homeGym?.reference?.id || '', ...(node.sports?.references?.nodes || []).map((item) => item.id), ...(node.notableFighters?.references?.nodes || []).map((item) => item.id)]); metadata = new Map([...metadata, ...(await loadMetadata(relationIds))]); const byId = new Map(indexRecords.map((record) => [record.id, record])); trainers = nodes.map((node) => mapTrainer(node, byId.get(node.id))).filter((trainer) => trainer?.name); if (trainers.length !== ids.length) throw new Error('Trainer hydration was incomplete'); const headCoaches = trainers.filter((trainer) => trainer.roles.includes('master')); renderGroup('headCoaches', headCoaches); Object.entries(sportAliases).forEach(([key, aliases]) => renderGroup(key, trainers.filter((trainer) => trainer.sports.some((sport) => aliases.includes(normalize(sport.label)) || aliases.includes(normalize(sport.handle)))))); render(); } catch (loadError) { setError(labels.error); } finally { setLoading(false); } })();
  });
})();




